import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, GraduationCap, CheckCircle2, XCircle,
  AlertTriangle, ShieldAlert, BookOpen, Trophy, RefreshCw,
} from "lucide-react";
import { driverApi } from "../lib/api";

const TOTAL_STEPS_INFO = "Læs alle moduler først, derefter starter testen.";

export default function DriverOnboardingPage() {
  const token = localStorage.getItem("ema_driver_token");
  const name = localStorage.getItem("ema_driver_name") || "Chauffør";
  const nav = useNavigate();
  const [status, setStatus] = React.useState(null);
  const [modules, setModules] = React.useState([]);
  const [phase, setPhase] = React.useState("loading"); // loading | locked | done | reading | test | result
  const [step, setStep] = React.useState(0);
  const [attemptId, setAttemptId] = React.useState(null);
  const [questions, setQuestions] = React.useState([]);
  const [answers, setAnswers] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      const [s, m] = await Promise.all([
        driverApi().get("/driver/onboarding/status"),
        driverApi().get("/driver/onboarding/modules").catch((err) => {
          if (err?.response?.status === 403) return { data: { modules: [] } };
          throw err;
        }),
      ]);
      setStatus(s.data);
      setModules(m.data?.modules || []);
      if (s.data.locked) setPhase("locked");
      else if (s.data.passed) setPhase("done");
      else setPhase("reading");
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem("ema_driver_token"); nav("/driver/login"); return; }
      toast.error("Kunne ikke hente onboarding");
    }
  }, [token, nav]);

  React.useEffect(() => { load(); }, [load]);

  if (!token) return <Navigate to="/driver/login" replace />;

  const startTest = async () => {
    try {
      const { data } = await driverApi().post("/driver/onboarding/start-test");
      setAttemptId(data.attempt_id);
      setQuestions(data.questions);
      setAnswers({});
      setResult(null);
      setPhase("test");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke starte testen");
    }
  };

  const submitTest = async () => {
    if (Object.keys(answers).length !== questions.length) {
      toast.error("Besvar alle spørgsmål før du indsender");
      return;
    }
    setSubmitting(true);
    try {
      const orderedAnswers = questions.map((q) => answers[q.id]);
      const { data } = await driverApi().post("/driver/onboarding/submit", {
        attempt_id: attemptId,
        answers: orderedAnswers,
      });
      setResult(data);
      setPhase("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Refresh status in the background
      driverApi().get("/driver/onboarding/status").then((r) => setStatus(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke indsende testen");
    } finally { setSubmitting(false); }
  };

  const logout = () => {
    localStorage.removeItem("ema_driver_token");
    localStorage.removeItem("ema_driver_name");
    nav("/driver/login");
  };

  // ------- Header -------
  const Header = () => (
    <header className="bg-brand-dark text-white py-5 px-5 flex items-center justify-between sticky top-0 z-30 border-b border-white/10" data-testid="onboarding-header">
      <div className="flex items-center gap-3">
        <GraduationCap size={22} className="text-brand-red" />
        <div className="leading-tight">
          <div className="font-display font-bold text-lg">Onboarding</div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-0.5">EMA Kurer · {name}</div>
        </div>
      </div>
      <button onClick={logout} className="text-xs text-white/70 hover:text-white underline underline-offset-4" data-testid="onboarding-logout">Log ud</button>
    </header>
  );

  // ------- Loading -------
  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-10 flex items-center justify-center text-brand-text2">
          <Loader2 className="animate-spin" size={20} /> <span className="ml-2">Henter onboarding...</span>
        </div>
      </div>
    );
  }

  // ------- Locked -------
  if (phase === "locked") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto p-6 mt-8" data-testid="onboarding-locked">
          <div className="bg-white border-2 border-red-200 p-8 shadow-[8px_8px_0_rgba(220,38,38,0.15)]">
            <div className="flex items-center gap-3 mb-4">
              <ShieldAlert size={32} className="text-brand-red" />
              <h1 className="font-display font-bold text-2xl text-brand-dark">Din konto er låst</h1>
            </div>
            <p className="text-brand-text2 text-sm leading-relaxed">
              Du har brugt begge dine forsøg på onboarding-testen uden at bestå. Af sikkerhedshensyn er din chauffør-konto nu låst, og du kan ikke modtage opgaver.
            </p>
            <div className="mt-6 bg-slate-50 border border-slate-200 p-5">
              <div className="font-semibold text-brand-dark text-sm">Hvad gør du nu?</div>
              <p className="text-sm text-brand-text2 mt-2">Kontakt EMA Kurer for at få adgang igen. Vi vil sammen gennemgå materialet og kan give dig et nyt forsøg.</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <a href="mailto:kontakt@emakurer.dk" className="btn-primary" data-testid="onboarding-locked-email">Skriv til kontakt@emakurer.dk</a>
                <a href="tel:+4570201234" className="btn-secondary">Ring til support</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ------- Done -------
  if (phase === "done") {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto p-6 mt-8" data-testid="onboarding-done">
          <div className="bg-white border-2 border-emerald-200 p-8 shadow-[8px_8px_0_rgba(16,185,129,0.15)]">
            <div className="flex items-center gap-3 mb-4">
              <Trophy size={32} className="text-emerald-600" />
              <h1 className="font-display font-bold text-2xl text-brand-dark">Onboarding gennemført</h1>
            </div>
            <p className="text-brand-text2 text-sm">Du har bestået onboarding-testen. Du kan nu modtage opgaver i appen.</p>
            <button onClick={() => nav("/driver")} className="btn-primary mt-6" data-testid="onboarding-go-dashboard">Til dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  // ------- Reading -------
  if (phase === "reading") {
    const mod = modules[step];
    const progress = modules.length ? ((step + 1) / modules.length) * 100 : 0;
    const isLast = step === modules.length - 1;
    return (
      <div className="min-h-screen bg-slate-50 pb-32">
        <Header />
        <div className="max-w-2xl mx-auto px-5 pt-6" data-testid="onboarding-reading">
          {/* Progress */}
          <div className="mb-5">
            <div className="flex items-center justify-between text-xs text-brand-text2 mb-2">
              <div className="flex items-center gap-2"><BookOpen size={14} /> Modul {step + 1} af {modules.length}</div>
              <div>{status?.attempts_remaining ?? 2} forsøg tilbage</div>
            </div>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-brand-red transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          {mod && (
            <article className="bg-white p-6 sm:p-8 border border-slate-200">
              <div className="kicker">Onboarding-modul · {mod.id}</div>
              <h1 className="font-display font-bold text-2xl sm:text-3xl text-brand-dark mt-2">{mod.title}</h1>
              <div className="mt-5 text-brand-text whitespace-pre-line leading-relaxed text-[15px]" data-testid={`onboarding-module-body-${mod.id}`}>{mod.body}</div>
            </article>
          )}

          {/* Nav */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 bg-white text-brand-dark font-semibold disabled:opacity-40"
              data-testid="onboarding-prev"
            >
              <ChevronLeft size={16} /> Forrige
            </button>
            {!isLast ? (
              <button
                onClick={() => { setStep((s) => Math.min(modules.length - 1, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="btn-primary"
                data-testid="onboarding-next"
              >
                Næste modul <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={startTest} className="btn-primary" data-testid="onboarding-start-test">
                Start testen <ChevronRight size={16} />
              </button>
            )}
          </div>

          <p className="text-center text-xs text-brand-text2 mt-6">{TOTAL_STEPS_INFO}</p>
        </div>
      </div>
    );
  }

  // ------- Test -------
  if (phase === "test") {
    const allAnswered = questions.every((q) => answers[q.id] !== undefined);
    return (
      <div className="min-h-screen bg-slate-50 pb-32">
        <Header />
        <div className="max-w-2xl mx-auto px-5 pt-6" data-testid="onboarding-test">
          <div className="bg-brand-dark text-white p-5 mb-6">
            <div className="kicker text-white/60">Afsluttende test</div>
            <h1 className="font-display font-bold text-xl mt-1">{questions.length} spørgsmål · 70% kræves</h1>
            <p className="text-white/70 text-xs mt-2">Du har {status?.attempts_remaining ?? 2} forsøg i alt. Læs hvert spørgsmål omhyggeligt.</p>
          </div>

          <div className="space-y-5">
            {questions.map((q, idx) => (
              <div key={q.id} className="bg-white border border-slate-200 p-5" data-testid={`onboarding-question-${q.id}`}>
                <div className="text-xs text-brand-text2 font-semibold mb-2">Spørgsmål {idx + 1}</div>
                <div className="font-semibold text-brand-dark text-[15px] leading-snug">{q.q}</div>
                <div className="mt-4 space-y-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[q.id] === oi;
                    return (
                      <label
                        key={oi}
                        className={`flex items-start gap-3 p-3 cursor-pointer border ${selected ? "border-brand-red bg-red-50" : "border-slate-200 hover:border-slate-400"}`}
                        data-testid={`onboarding-option-${q.id}-${oi}`}
                      >
                        <input
                          type="radio"
                          name={`q-${q.id}`}
                          checked={selected}
                          onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                          className="mt-1 accent-brand-red"
                        />
                        <span className="text-sm text-brand-dark">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              onClick={() => setPhase("reading")}
              className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 bg-white text-brand-dark font-semibold"
              data-testid="onboarding-back-to-reading"
            >
              <ChevronLeft size={16} /> Tilbage til materiale
            </button>
            <button
              onClick={submitTest}
              disabled={submitting || !allAnswered}
              className="btn-primary disabled:opacity-50"
              data-testid="onboarding-submit"
            >
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Indsender...</> : <>Indsend test</>}
            </button>
          </div>
          {!allAnswered && (
            <p className="text-center text-xs text-brand-text2 mt-3">Besvar alle {questions.length} spørgsmål før du kan indsende.</p>
          )}
        </div>
      </div>
    );
  }

  // ------- Result -------
  if (phase === "result" && result) {
    const passed = result.passed;
    const locked = result.locked;
    return (
      <div className="min-h-screen bg-slate-50 pb-16">
        <Header />
        <div className="max-w-2xl mx-auto px-5 pt-6" data-testid="onboarding-result">
          <div className={`p-6 border-2 ${passed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-center gap-3">
              {passed ? <Trophy size={32} className="text-emerald-600" /> : (locked ? <ShieldAlert size={32} className="text-brand-red" /> : <AlertTriangle size={32} className="text-amber-600" />)}
              <div>
                <h1 className="font-display font-bold text-2xl text-brand-dark">
                  {passed ? "Bestået!" : (locked ? "Konto låst" : "Ikke bestået")}
                </h1>
                <p className="text-brand-text2 text-sm mt-1">
                  {result.correct} / {result.total} korrekte svar (krav: {result.pass_threshold}/10)
                </p>
              </div>
            </div>

            {passed && (
              <div className="mt-5">
                <p className="text-emerald-800 text-sm">Tillykke. Du kan nu modtage opgaver i EMA Kurer Driver-appen. Vi har sendt dit personlige certifikat til din email.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button onClick={() => nav("/driver")} className="btn-primary" data-testid="onboarding-result-go-dashboard">Til dashboard</button>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/driver/onboarding/certificate`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      // axios sets auth via headers but anchor cannot — fall back to fetch + blob
                      e.preventDefault();
                      driverApi().get("/driver/onboarding/certificate", { responseType: "blob" })
                        .then((r) => {
                          const url = URL.createObjectURL(r.data);
                          window.open(url, "_blank");
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        })
                        .catch(() => toast.error("Kunne ikke hente certifikat"));
                    }}
                    className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 bg-white text-brand-dark font-semibold"
                    data-testid="onboarding-download-certificate"
                  >
                    Download certifikat
                  </a>
                </div>
              </div>
            )}

            {!passed && locked && (
              <div className="mt-5">
                <p className="text-red-800 text-sm">Du har brugt begge forsøg. Din konto er låst. Kontakt <a href="mailto:kontakt@emakurer.dk" className="font-semibold underline">kontakt@emakurer.dk</a> for at få genåbnet din konto.</p>
              </div>
            )}

            {!passed && !locked && (
              <div className="mt-5">
                <p className="text-amber-800 text-sm">Du har <strong>1 forsøg tilbage</strong>. Læs materialet grundigt igennem igen, og prøv så testen på ny.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button onClick={() => { setStep(0); setPhase("reading"); }} className="btn-primary" data-testid="onboarding-retry-read">
                    <BookOpen size={16} /> Læs materialet igen
                  </button>
                  <button onClick={startTest} className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 bg-white text-brand-dark font-semibold" data-testid="onboarding-retry-test">
                    <RefreshCw size={16} /> Prøv testen igen
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Review */}
          {result.review && result.review.length > 0 && (
            <details className="mt-6 bg-white border border-slate-200 p-5" data-testid="onboarding-review">
              <summary className="cursor-pointer font-semibold text-brand-dark text-sm">Se gennemgang af dine svar</summary>
              <div className="mt-4 space-y-3">
                {result.review.map((r, i) => (
                  <div key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: r.is_correct ? "#10b981" : "#dc2626" }}>
                    <div className="flex items-center gap-2 text-xs">
                      {r.is_correct ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-brand-red" />}
                      <span className="font-semibold text-brand-dark">Spørgsmål {i + 1}</span>
                    </div>
                    <div className="text-sm text-brand-dark mt-1">{r.q}</div>
                    <div className="text-xs text-brand-text2 mt-1">
                      Dit svar: <span className={r.is_correct ? "text-emerald-700" : "text-red-700"}>{r.your_answer || "—"}</span>
                    </div>
                    {!r.is_correct && (
                      <div className="text-xs text-emerald-700 mt-0.5">Korrekt: {r.correct_answer}</div>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    );
  }

  return null;
}
